import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, CheckCircle2, Circle, Download, Loader2, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SlideRenderStatus } from '@/hooks/use-carousel-export';

interface ExportProgressDialogProps {
  open: boolean;
  progress: SlideRenderStatus[];
  /** A render (or retry) is in flight: the dialog cannot be dismissed. */
  running: boolean;
  /** The ZIP is being assembled. */
  downloading: boolean;
  /** Inline error (already translated), e.g. the ZIP failed to build. */
  errorMessage: string | null;
  onRetry: (index: number) => void;
  onDownload: () => void;
  onClose: () => void;
}

const STATUS_ICON: Record<SlideRenderStatus['status'], React.ReactNode> = {
  pending: <Circle className="h-4 w-4 text-muted-foreground" />,
  rendering: <Loader2 className="h-4 w-4 animate-spin text-primary" />,
  done: <CheckCircle2 className="h-4 w-4 text-primary" />,
  failed: <AlertCircle className="h-4 w-4 text-destructive" />,
};

/** Progress of the slide still renders. Not dismissible while rendering (no
 *  X, Escape or overlay click); once idle it lists failed slides with a
 *  per-slide retry and enables the download only when every slide is done. */
export const ExportProgressDialog: React.FC<ExportProgressDialogProps> = ({
  open,
  progress,
  running,
  downloading,
  errorMessage,
  onRetry,
  onDownload,
  onClose,
}) => {
  const { t } = useTranslation();
  const busy = running || downloading;
  const done = progress.filter((entry) => entry.status === 'done').length;
  const total = progress.length;
  const allDone = total > 0 && done === total;
  const blockDismiss = (event: Event) => {
    if (busy) event.preventDefault();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton={busy}
        onEscapeKeyDown={blockDismiss}
        onPointerDownOutside={blockDismiss}
        onInteractOutside={blockDismiss}
        data-testid="quote-export-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('quoteCarousel.export.title')}</DialogTitle>
          <DialogDescription data-testid="quote-export-progress">
            {t('quoteCarousel.export.generating', { done, total })}
          </DialogDescription>
        </DialogHeader>

        <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto" data-testid="quote-export-slides">
          {progress.map((entry) => (
            <li
              key={entry.index}
              className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
              data-testid={`quote-export-slide-${entry.index}`}
              data-status={entry.status}
            >
              {STATUS_ICON[entry.status]}
              <span className="flex-1">
                {t('quoteCarousel.slides.slideLabel', { number: entry.index + 1 })}
              </span>
              <span className="text-xs text-muted-foreground">
                {t(`quoteCarousel.export.status.${entry.status}`)}
              </span>
              {entry.status === 'failed' && !busy && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  data-testid={`quote-export-retry-${entry.index}`}
                  onClick={() => onRetry(entry.index)}
                >
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  {t('quoteCarousel.export.retry')}
                </Button>
              )}
            </li>
          ))}
        </ul>

        {busy && (
          <p className="text-xs text-muted-foreground">{t('quoteCarousel.export.keepTabOpen')}</p>
        )}
        {!busy && !allDone && total > 0 && (
          <p className="text-xs text-destructive" data-testid="quote-export-failed-hint">
            {t('quoteCarousel.export.failedHint')}
          </p>
        )}
        {errorMessage && (
          <p className="text-xs text-destructive" data-testid="quote-export-error">{errorMessage}</p>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="secondary" disabled={busy} onClick={onClose}>
            {t('quoteCarousel.export.close')}
          </Button>
          <Button
            data-testid="quote-export-download"
            disabled={busy || !allDone}
            onClick={onDownload}
          >
            {downloading ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-1 h-4 w-4" />
            )}
            {t('quoteCarousel.export.downloadZip')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportProgressDialog;
